import * as React from "react";
import * as S from "@styled";
import * as ProcessListActions from "actions/ProcessListActions";
import { OrganizationBase } from "model/Organization";
import { AppContext } from "App";
import useUserData from "hooks/useUserData";
import { Process } from "model/process/Process";
import { Types } from "reducers/AppReducer";
import usePutMutation from "hooks/usePutMutation";
import useDeferredMutation from "hooks/useDeferredMutation";
import ContainerWithLoadingSpinner from "components/shared/ContainerWithLoadingSpinner";
import Text from "components/shared/Text";
import IntegrationFilters from "components/integrations/IntegrationFilters";
import Divider from "components/shared/Divider";
import Button, {
    CloseButton,
    DropdownButton,
    SaveButton,
} from "components/shared/Button";
import Toggle from "components/shared/Toggle";
import Link from "components/shared/Link";
import {
    INTEGRATION_CATALOG_BASE_PATH,
    INTEGRATIONS_BASE_PATH,
} from "AppRoutes";
import useQuery from "hooks/useQuery";
import { PagedResponse } from "model/PagedResponse";
import { CountProps, PROCESSES_APIROUTE } from "./Integrations";
import useIntersectionObserver from "hooks/useIntersectionObserver";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import Tooltip from "components/shared/Tooltip";

interface ProcessListProps {
    organization: OrganizationBase;
    onProcessDeleteClick: (processId: number) => void;
}

const ProcessList: React.FC<ProcessListProps> = (props) => {
    const { dispatch, state } = React.useContext(AppContext);
    const queryClient = useQueryClient();

    const stateProcesses = state.integrations.processes;
    const { selectedOrganization } = useUserData();

    const loadMoreProcessesRef = React.useRef<HTMLDivElement | null>(null);

    const isListViewStorageKey = "frends_bap_my_integrations_is_list_view";

    const [tagFilter, setTagFilter] = React.useState<string[]>();
    const [searchFilter, setSearchFilter] = React.useState<string>("");

    const initialIsListView =
        String(localStorage.getItem(isListViewStorageKey)) === "true";
    const [isListView, setIsListView] = React.useState(initialIsListView);

    const [pageNumber, setPageNumber] = React.useState<number>(1);

    const processQueryRoute =
        selectedOrganization &&
        `${PROCESSES_APIROUTE}/organizations/${selectedOrganization.id}`;
    const processesQuery = useQuery<PagedResponse<Process>>(
        processQueryRoute!,
        {
            pageNumber: pageNumber,
            processName: searchFilter,
            pageSize: 15,
            ...(tagFilter && { tags: tagFilter }),
        },
        {
            enabled: processQueryRoute !== undefined,
        },
        "Failed to fetch integrations.",
    );

    const organizationProcessTagsQuery = useQuery<string[]>(
        `${PROCESSES_APIROUTE}/${selectedOrganization?.id}/process-tags`,
        undefined,
        {
            enabled: selectedOrganization !== undefined,
        },
        "Failed to fetch process tags.",
    );

    const organizationProcessTags = organizationProcessTagsQuery.data || [];

    const isLoading = processesQuery.isLoading;

    React.useEffect(() => {
        if (processesQuery.isError && stateProcesses.length !== 0) {
            ProcessListActions.setProcesses(dispatch, []);
            return;
        }
        if (processesQuery.data?.data) {
            if (pageNumber > 1) {
                ProcessListActions.appendProcesses(
                    dispatch,
                    processesQuery.data.data,
                );
                return;
            }

            ProcessListActions.setProcesses(dispatch, processesQuery.data.data);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [processesQuery.isError, processesQuery.data, pageNumber]);

    const totalProcessesLength = processesQuery.data
        ? processesQuery.data.paging.totalCount
        : 0;

    const hasMoreProcesses =
        processesQuery.data != null &&
        stateProcesses.length > 0 &&
        totalProcessesLength > stateProcesses.length;

    useIntersectionObserver({
        blockIntersectAction: processesQuery.isLoading || !hasMoreProcesses,
        intersectionObserverDependencies: [
            processesQuery.isLoading,
            hasMoreProcesses,
            stateProcesses.length,
            totalProcessesLength,
            queryClient,
        ],
        intersectionRef: loadMoreProcessesRef,
        threshold: 0.5,
        onIntersect: () => {
            if (hasMoreProcesses) {
                setPageNumber((prevPage) => prevPage + 1);
                queryClient.invalidateQueries(processQueryRoute);
            }
        },
    });

    const fetchInitialProcesses = () => {
        setPageNumber(1);
        ProcessListActions.setProcesses(dispatch, []);
        queryClient.invalidateQueries(processQueryRoute);
    };

    const handleChangeTagFilter = (nextValues: string[]) => {
        if (nextValues.length === 0) {
            setTagFilter(undefined);
        } else {
            setPageNumber(1);
            setTagFilter(nextValues);
        }
        if (tagFilter && nextValues.length === 0) {
            fetchInitialProcesses();
        }
    };

    const handleChangeSearchFilter = (nextValue: string) => {
        setPageNumber(1);
        setSearchFilter(nextValue);
        if (searchFilter && !nextValue) {
            fetchInitialProcesses();
        }
    };

    const handleChangeLayout = (next: boolean) => {
        setIsListView(next);
        localStorage.setItem(isListViewStorageKey, String(next));
    };

    return (
        <>
            <IntegrationFilters
                tags={organizationProcessTags}
                isListView={isListView}
                searchFilter={searchFilter}
                tagFilter={tagFilter}
                onChangeLayout={handleChangeLayout}
                onChangeSearchFilter={handleChangeSearchFilter}
                onChangeTagFilter={handleChangeTagFilter}
            />

            <ContainerWithLoadingSpinner
                loading={isLoading}
                style={{ minHeight: "5rem" }}
            >
                {stateProcesses && (
                    <S.MyIntegrationsList $cols={isListView ? 1 : 3}>
                        {stateProcesses.map((p, idx) => (
                            <React.Fragment key={idx}>
                                {isListView && p.isBapProcess && (
                                    <LargeIntegrationsListItem
                                        process={p}
                                        organization={props.organization}
                                        onDeleteClick={
                                            props.onProcessDeleteClick
                                        }
                                    />
                                )}

                                {isListView && !p.isBapProcess && (
                                    <LargeNonBapIntegrationsListItem
                                        process={p}
                                        organization={props.organization}
                                    />
                                )}

                                {!isListView && p.isBapProcess && (
                                    <IntegrationsListItem
                                        process={p}
                                        organization={props.organization}
                                        onDeleteClick={
                                            props.onProcessDeleteClick
                                        }
                                    />
                                )}

                                {!isListView && !p.isBapProcess && (
                                    <NonBapIntegrationsListItem
                                        process={p}
                                        organization={props.organization}
                                    />
                                )}
                            </React.Fragment>
                        ))}
                    </S.MyIntegrationsList>
                )}

                {!isLoading && stateProcesses.length === 0 && (
                    <Text tag="h3">No integrations found</Text>
                )}
                {hasMoreProcesses && (
                    <div ref={loadMoreProcessesRef} style={{ height: "1px" }} />
                )}
            </ContainerWithLoadingSpinner>
        </>
    );
};

interface IntegrationsListItemProps extends IntegrationsListItemViewOnlyProps {
    onDeleteClick: (processId: number) => void;
}

interface IntegrationsListItemViewOnlyProps {
    process: Process;
    organization: OrganizationBase;
}

const IntegrationsListItem: React.FC<IntegrationsListItemProps> = ({
    process,
    organization,
    onDeleteClick,
}) => {
    const [actionsOpen, setActionsOpen] = React.useState(false);

    const toggleActionsOpen = () => setActionsOpen((prev) => !prev);

    return (
        <S.MyIntegrationsListItem>
            <Text tag="h3">{process.name}</Text>

            <Divider />

            {actionsOpen ? (
                <S.MyIntegrationsListItemActiveToggleContainer>
                    <IntegrationActivateToggle process={process} />

                    <Link
                        type="button"
                        onClick={() => onDeleteClick(process.processId)}
                        text="Delete"
                    />
                </S.MyIntegrationsListItemActiveToggleContainer>
            ) : (
                <S.MyIntegrationsListItemKPIContainer>
                    <Count
                        value={process.totalExecutionCount}
                        label="Executions"
                    />
                    <Count
                        value={process.failedExecutionCount}
                        label="Errors"
                    />
                </S.MyIntegrationsListItemKPIContainer>
            )}

            <S.MyIntegrationsListItemActionsContainer
                $justifyContent={actionsOpen ? "space-evenly" : "space-between"}
            >
                <ViewInstancesLink
                    process={process}
                    organization={organization}
                />

                {actionsOpen ? (
                    <>
                        <ManuallyRunLink
                            process={process}
                            organization={organization}
                        />

                        <IntegrationUpdateButton process={process} />

                        <ReconfigureLink
                            process={process}
                            organization={organization}
                        />

                        <CloseButton
                            title="Hide actions"
                            onClick={toggleActionsOpen}
                        />
                    </>
                ) : (
                    <DropdownButton
                        title="Show actions"
                        onClick={toggleActionsOpen}
                    >
                        Actions
                    </DropdownButton>
                )}
            </S.MyIntegrationsListItemActionsContainer>
        </S.MyIntegrationsListItem>
    );
};

const NonBapIntegrationsListItem: React.FC<
    IntegrationsListItemViewOnlyProps
> = ({ process, organization }) => {
    return (
        <S.MyIntegrationsListItem>
            <S.MyIntegrationsListItemViewOnlyTitleContainer>
                <Text tag="h3">{process.name}</Text>

                <ViewOnlyTooltip />
            </S.MyIntegrationsListItemViewOnlyTitleContainer>

            <Divider />

            <S.MyIntegrationsListItemKPIContainer>
                <Count value={process.totalExecutionCount} label="Executions" />
                <Count value={process.failedExecutionCount} label="Errors" />
            </S.MyIntegrationsListItemKPIContainer>

            <S.MyIntegrationsListItemActionsContainer $justifyContent="space-between">
                <ViewInstancesLink
                    process={process}
                    organization={organization}
                />

                <ManuallyRunLink
                    process={process}
                    organization={organization}
                />
            </S.MyIntegrationsListItemActionsContainer>
        </S.MyIntegrationsListItem>
    );
};

const ViewOnlyTooltip: React.FC = () => (
    <Tooltip toggleText="View only">
        Integration maintained by portal admin
    </Tooltip>
);

const LargeIntegrationsListItem: React.FC<IntegrationsListItemProps> = ({
    process,
    organization,
    onDeleteClick,
}) => {
    return (
        <S.MyIntegrationsLargeListItem>
            <S.MyIntegrationsLargeListItemTitleContainer>
                <IntegrationActivateToggle process={process} hidelabel />

                <Text tag="h2">{process.name}</Text>
            </S.MyIntegrationsLargeListItemTitleContainer>

            <Text tag="p">{process.description}</Text>

            <S.MyIntegrationsLargeListItemActionContainer>
                <S.MyIntegrationsLargeListItemActionContainer>
                    <ViewInstancesLink
                        process={process}
                        organization={organization}
                    />

                    <ManuallyRunLink
                        process={process}
                        organization={organization}
                    />
                </S.MyIntegrationsLargeListItemActionContainer>

                <S.MyIntegrationsLargeListItemActionContainer>
                    <Link
                        type="button"
                        onClick={() => onDeleteClick(process.processId)}
                        text="Delete"
                    />

                    <IntegrationUpdateButton process={process} />

                    <ReconfigureLink
                        process={process}
                        organization={organization}
                    />
                </S.MyIntegrationsLargeListItemActionContainer>
            </S.MyIntegrationsLargeListItemActionContainer>
        </S.MyIntegrationsLargeListItem>
    );
};

const LargeNonBapIntegrationsListItem: React.FC<
    IntegrationsListItemViewOnlyProps
> = ({ process, organization }) => {
    return (
        <S.MyIntegrationsLargeListItem>
            <S.MyIntegrationsLargeListItemTitleContainer>
                <Text tag="h2">{process.name}</Text>

                <ViewOnlyTooltip />
            </S.MyIntegrationsLargeListItemTitleContainer>

            <Text tag="p">{process.description}</Text>

            <S.MyIntegrationsLargeListItemActionContainer>
                <S.MyIntegrationsLargeListItemActionContainer>
                    <ViewInstancesLink
                        process={process}
                        organization={organization}
                    />

                    <ManuallyRunLink
                        process={process}
                        organization={organization}
                    />
                </S.MyIntegrationsLargeListItemActionContainer>
            </S.MyIntegrationsLargeListItemActionContainer>
        </S.MyIntegrationsLargeListItem>
    );
};
interface IntegrationActivateToggleProps {
    process: Process;
    hidelabel?: boolean;
}

const IntegrationActivateToggle: React.FC<IntegrationActivateToggleProps> = (
    props,
) => {
    const [active, setActive] = React.useState(props.process.triggersActive);

    const activate = useDeferredMutation(
        "PUT",
        undefined,
        "Failed to activate process.",
    );

    React.useEffect(() => {
        if (activate.error) {
            setActive((prev) => !prev);
        }
    }, [activate.error]);

    const toggleActive = () => {
        activate.reset();
        activate.mutate({
            url: `${PROCESSES_APIROUTE}/${props.process.productionDeploymentId}/${active ? "disable" : "enable"}`,
        });
        setActive(!active);
    };

    return (
        <ContainerWithLoadingSpinner loading={activate.isLoading}>
            <Toggle
                label="Active"
                hidelabel={props.hidelabel}
                ontext="ON"
                offtext="OFF"
                checked={active}
                onChange={toggleActive}
            />
        </ContainerWithLoadingSpinner>
    );
};

type IntegrationLinkProps = Omit<IntegrationLinkBaseProps, "href">;

const ViewInstancesLink: React.FC<IntegrationLinkProps> = (props) => (
    <IntegrationLinkBase
        href={`${INTEGRATIONS_BASE_PATH}/${props.process.processId}/instances`}
        process={props.process}
        organization={props.organization}
    >
        View instances
    </IntegrationLinkBase>
);

const ManuallyRunLink: React.FC<IntegrationLinkProps> = (props) => (
    <IntegrationLinkBase
        href={`${INTEGRATIONS_BASE_PATH}/${props.process.processId}/manually-run`}
        process={props.process}
        organization={props.organization}
    >
        Manual run
    </IntegrationLinkBase>
);

const ReconfigureLink: React.FC<IntegrationLinkProps> = (props) => (
    <IntegrationLinkBase
        href={`${INTEGRATION_CATALOG_BASE_PATH}/${props.process.templateId}/setup`}
        process={props.process}
        organization={props.organization}
    >
        Reconfigure
    </IntegrationLinkBase>
);

interface IntegrationLinkBaseProps {
    href: string;
    process: Process;
    organization: OrganizationBase;
}

const IntegrationLinkBase: React.FC<
    React.PropsWithChildren<IntegrationLinkBaseProps>
> = (props) => {
    const navigate = useNavigate();

    const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();

        navigate(event.currentTarget.href, {
            state: {
                process: props.process,
                organization: props.organization,
                previousPage: INTEGRATIONS_BASE_PATH,
            },
        });
    };

    return (
        <Button
            type="link"
            variant="bordered"
            onClick={handleClick}
            href={props.href}
        >
            {props.children}
        </Button>
    );
};

const IntegrationUpdateButton: React.FC<{ process: Process }> = ({
    process,
}) => {
    const { dispatch } = React.useContext(AppContext);
    const update = usePutMutation<Process>(
        `${PROCESSES_APIROUTE}/${process.processId}`,
        { templateId: process.templateId },
        undefined,
        "Failed to update process.",
    );

    React.useEffect(() => {
        if (update.data) {
            dispatch({
                type: Types.UPDATE_INTEGRATION,
                payload: {
                    processId: process.processId,
                    updatedProcess: update.data,
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [update.data]);

    const handleUpdateClick = () => {
        if (!update.isLoading) {
            update.reset();
            update.mutate();
        }
    };

    return (
        <SaveButton
            variant="bordered"
            onClick={handleUpdateClick}
            loading={update.isLoading}
            success={update.isSuccess}
            error={update.isError}
        >
            Update
        </SaveButton>
    );
};

const Count: React.FC<CountProps> = ({ value = "-", label }) => (
    <S.MyIntegrationsCountLarge>
        <Text tag="span" appearance="h2" size="bold">
            {value}
        </Text>

        <br />

        <Text tag="span" appearance="label">
            {label}
        </Text>
    </S.MyIntegrationsCountLarge>
);

export default ProcessList;
